class ProductViewerMediaGallery extends HTMLElement {
  #controller;
  #activeCardIndex = 0;
  #isNavigating = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Desktop: panel card clicks
    this.querySelectorAll('.pvmg__card').forEach((card) => {
      card.addEventListener('click', (e) => {
        const cardIdx = parseInt(e.currentTarget.dataset.cardIndex, 10);
        this.#setActiveCard(cardIdx);
      }, { signal });
    });

    // Mobile: carousel
    this.#initMobileCarousel(signal);

    // Activate first card
    this.#setActiveCard(0);
    this.#updateActiveCarouselCard(0);
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /* === MOBILE CAROUSEL === */

  #initMobileCarousel(signal) {
    const track = this.querySelector('.pvmg__carousel-track');
    if (!track) return;

    // Scroll sync — detect center card on swipe
    let ticking = false;
    track.addEventListener('scroll', () => {
      if (this.#isNavigating || ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const cards = track.querySelectorAll('.pvmg__carousel-card');
        const scrollCenter = track.scrollLeft + track.offsetWidth / 2;
        let newIndex = 0;

        cards.forEach((card, i) => {
          const cardCenter = card.offsetLeft + card.offsetWidth / 2;
          const currentBest = cards[newIndex];
          const bestCenter = currentBest.offsetLeft + currentBest.offsetWidth / 2;
          if (Math.abs(cardCenter - scrollCenter) < Math.abs(bestCenter - scrollCenter)) {
            newIndex = i;
          }
        });

        if (newIndex !== this.#activeCardIndex) {
          this.#switchBackground(newIndex);
          this.#activeCardIndex = newIndex;
          this.#updateActiveDot(newIndex);
          this.#updateActiveCarouselCard(newIndex);
        }
        ticking = false;
      });
    }, { signal, passive: true });

    // Dot clicks
    this.querySelectorAll('.pvmg__dot').forEach((dot) => {
      dot.addEventListener('click', (e) => {
        const cardIdx = parseInt(e.currentTarget.dataset.cardIndex, 10);
        this.#navigateCarouselCard(cardIdx);
      }, { signal });
    });

    // Carousel card clicks — navigate to clicked card
    track.querySelectorAll('.pvmg__carousel-card').forEach((card) => {
      card.addEventListener('click', (e) => {
        const cardIdx = parseInt(e.currentTarget.dataset.cardIndex, 10);
        if (cardIdx !== this.#activeCardIndex) {
          this.#navigateCarouselCard(cardIdx);
        }
      }, { signal });
    });
  }

  #navigateCarouselCard(cardIdx) {
    const track = this.querySelector('.pvmg__carousel-track');
    if (!track) return;

    const cardCount = parseInt(track.dataset.cardCount, 10) || 0;
    cardIdx = Math.max(0, Math.min(cardIdx, cardCount - 1));

    const card = track.querySelector(`.pvmg__carousel-card[data-card-index="${cardIdx}"]`);
    if (!card) return;

    // Update state
    this.#switchBackground(cardIdx);
    this.#activeCardIndex = cardIdx;
    this.#updateActiveDot(cardIdx);
    this.#updateActiveCarouselCard(cardIdx);

    // Also update desktop panel card (keeps them in sync)
    this.#setActivePanelCard(cardIdx);

    // Programmatic scroll — block scroll listener
    this.#isNavigating = true;
    track.scrollTo({
      left: card.offsetLeft + card.offsetWidth / 2 - track.offsetWidth / 2,
      behavior: 'smooth',
    });

    // Re-enable scroll sync after scroll settles
    let scrollEndTimer;
    const onScrollEnd = () => {
      clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => {
        this.#isNavigating = false;
        track.removeEventListener('scroll', onScrollEnd);
      }, 100);
    };
    track.addEventListener('scroll', onScrollEnd, { passive: true });
  }

  #updateActiveDot(idx) {
    this.querySelectorAll('.pvmg__dot').forEach((dot) => {
      dot.setAttribute(
        'aria-selected',
        parseInt(dot.dataset.cardIndex, 10) === idx ? 'true' : 'false'
      );
    });
  }

  #updateActiveCarouselCard(idx) {
    this.querySelectorAll('.pvmg__carousel-card').forEach((card) => {
      card.classList.toggle(
        'pvmg__carousel-card--active',
        parseInt(card.dataset.cardIndex, 10) === idx
      );
    });
  }

  /* === DESKTOP PANEL === */

  #setActiveCard(cardIdx) {
    if (cardIdx === this.#activeCardIndex) {
      const card = this.querySelector(`.pvmg__card[data-card-index="${cardIdx}"]`);
      if (card && card.classList.contains('pvmg__card--active')) return;
    }

    this.#switchBackground(cardIdx);
    this.#activeCardIndex = cardIdx;

    this.#setActivePanelCard(cardIdx);

    // Also sync mobile carousel
    this.#updateActiveDot(cardIdx);
    this.#updateActiveCarouselCard(cardIdx);
    this.#scrollCarouselToCard(cardIdx);
  }

  #setActivePanelCard(cardIdx) {
    const cards = this.querySelectorAll('.pvmg__card');
    const newCard = this.querySelector(`.pvmg__card[data-card-index="${cardIdx}"]`);

    // Phase 1: deactivate all cards (body collapses at 0s — instant)
    cards.forEach((c) => c.classList.remove('pvmg__card--active'));

    // Phase 2: activate new card in next frame (prevents height overlap)
    requestAnimationFrame(() => {
      if (newCard) newCard.classList.add('pvmg__card--active');
    });
  }

  #scrollCarouselToCard(cardIdx) {
    const track = this.querySelector('.pvmg__carousel-track');
    if (!track) return;

    const card = track.querySelector(`.pvmg__carousel-card[data-card-index="${cardIdx}"]`);
    if (!card) return;

    this.#isNavigating = true;
    track.scrollTo({
      left: card.offsetLeft + card.offsetWidth / 2 - track.offsetWidth / 2,
      behavior: 'smooth',
    });

    let scrollEndTimer;
    const onScrollEnd = () => {
      clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => {
        this.#isNavigating = false;
        track.removeEventListener('scroll', onScrollEnd);
      }, 100);
    };
    track.addEventListener('scroll', onScrollEnd, { passive: true });
  }

  /* === BACKGROUND TRANSITION === */

  #switchBackground(newIndex) {
    if (newIndex === this.#activeCardIndex) return;

    const forward = newIndex > this.#activeCardIndex;
    const slide = getComputedStyle(this).getPropertyValue('--pvmg-transition-slide').trim() || '15%';
    this.style.setProperty('--pvmg-slide-leave', forward ? `-${slide}` : slide);
    this.style.setProperty('--pvmg-slide-enter', forward ? slide : `-${slide}`);

    this.querySelectorAll('.pvmg__bg').forEach((bg) => {
      const idx = parseInt(bg.dataset.cardIndex, 10);
      if (idx === this.#activeCardIndex) {
        bg.classList.remove('pvmg__bg--active');
        bg.classList.add('pvmg__bg--leaving');
      } else if (idx === newIndex) {
        bg.classList.remove('pvmg__bg--leaving');
        bg.classList.add('pvmg__bg--active');
      }
    });

    const speed = parseInt(getComputedStyle(this).getPropertyValue('--pvmg-transition-speed'), 10) || 500;
    setTimeout(() => {
      this.querySelectorAll('.pvmg__bg--leaving').forEach((v) => {
        v.classList.remove('pvmg__bg--leaving');
      });
    }, speed + 300);
  }
}

if (!customElements.get('product-viewer-media-gallery')) {
  customElements.define('product-viewer-media-gallery', ProductViewerMediaGallery);
}
